import { ReferenceFields } from 'honeygrid-types'

import type { PaletteColorOptions, ThemeOptions } from '@mui/material/styles'

interface BasePalette {
	[ReferenceFields.CustomerCount]: PaletteColorOptions
	[ReferenceFields.CustomerSales]: PaletteColorOptions
	[ReferenceFields.CustomerMarketShare]: PaletteColorOptions
	[ReferenceFields.Households]: PaletteColorOptions
	[ReferenceFields.HomeValues]: PaletteColorOptions
	[ReferenceFields.Income]: PaletteColorOptions
	[ReferenceFields.NewMoverTotal]: PaletteColorOptions
	[ReferenceFields.NewMoverSFDU]: PaletteColorOptions
	[ReferenceFields.NewMoverMFDU]: PaletteColorOptions
	[ReferenceFields.SFDU]: PaletteColorOptions
	[ReferenceFields.MFDU]: PaletteColorOptions
}

interface BasePropsColorOverrides {
	[ReferenceFields.CustomerCount]: true
	[ReferenceFields.CustomerSales]: true
	[ReferenceFields.CustomerMarketShare]: true
	[ReferenceFields.Households]: true
	[ReferenceFields.HomeValues]: true
	[ReferenceFields.Income]: true
	[ReferenceFields.NewMoverTotal]: true
	[ReferenceFields.NewMoverSFDU]: true
	[ReferenceFields.NewMoverMFDU]: true
	[ReferenceFields.SFDU]: true
	[ReferenceFields.MFDU]: true
}

declare module '@mui/material/styles' {
	interface Palette extends BasePalette {}

	interface PaletteOptions extends BasePalette {}
}

declare module '@mui/material/Button' {
	interface ButtonPropsColorOverrides extends BasePropsColorOverrides {}
}

declare module '@mui/material/ButtonGroup' {
	interface ButtonGroupPropsColorOverrides extends BasePropsColorOverrides {}
}

// Brand colors from the design system
export const brandColors = {
	primary: '#3A2E2F', // Primary brand color (dark brown)
	primaryContrast: '#FFFDFD', // Primary contrast (almost white)
	primaryAction: '#621984', // Primary action (purple)
	primaryLight: '#81479C', // Primary light (light purple)
	hexGrid: '#878DDD', // Hex grid primary (periwinkle blue)
} as const

export const referenceFieldColors = {
	[ReferenceFields.CustomerCount]: {
		main: '#792121',
		contrastText: '#FFFFFF',
	},
	[ReferenceFields.CustomerSales]: {
		main: '#2E623F',
		contrastText: '#FFFFFF',
	},
	[ReferenceFields.CustomerMarketShare]: {
		main: '#A65900',
		contrastText: '#FFFFFF',
	},
	[ReferenceFields.Households]: {
		main: '#878DDD', // Using hex grid primary color
		contrastText: '#FFFFFF',
	},
	[ReferenceFields.HomeValues]: {
		main: '#235D6A',
		contrastText: '#FFFFFF',
	},
	[ReferenceFields.Income]: {
		main: '#878DDD', // Using hex grid primary color
		contrastText: '#FFFFFF',
	},
	[ReferenceFields.NewMoverTotal]: {
		main: '#792121',
		contrastText: '#FFFFFF',
	},
	[ReferenceFields.NewMoverSFDU]: {
		main: '#A65900',
		contrastText: '#FFFFFF',
	},
	[ReferenceFields.NewMoverMFDU]: {
		main: '#2E623F',
		contrastText: '#FFFFFF',
	},
	[ReferenceFields.SFDU]: {
		main: '#621984', // Using primary action color
		contrastText: '#FFFFFF',
	},
	[ReferenceFields.MFDU]: {
		main: '#235D6A',
		contrastText: '#FFFFFF',
	},
	default: {
		main: '#792121',
		contrastText: '#FFFFFF',
	},
} as const

export enum Theme {
	Light = 'light',
	Dark = 'dark',
}

export const MAP_THEMES = {
	[Theme.Light]: 'mapbox://styles/honeygrid/cllfbfowr00gj01pu1fgsc7o4',
	[Theme.Dark]: 'mapbox://styles/honeygrid/clkv8xaa8001601q0ba0r3y9r',
}

export const componentOverrides: ThemeOptions['components'] = {
	MuiButton: {
		styleOverrides: {
			root: {
				textTransform: 'none',
				borderRadius: '1000px',
			},
			outlined: {
				borderWidth: '2px',
				'&:hover': {
					borderWidth: '2px',
				},
			},
		},
	},
	MuiTypography: {
		styleOverrides: {
			h1: {
				letterSpacing: '-1.5px',
				fontWeight: '600',
			},
			h2: {
				letterSpacing: '-0.5px',
				fontWeight: '600',
			},
			h4: {
				letterSpacing: '0.25px',
			},
			h6: {
				fontWeight: '600',
				letterSpacing: '0.15px',
			},
			subtitle1: {
				letterSpacing: '0.15px',
				fontWeight: '600',
			},
			subtitle2: {
				letterSpacing: '0.1px',
				fontWeight: '600',
			},
			body1: {
				letterSpacing: '0.15px',
			},
			body2: {
				letterSpacing: '0.15px',
			},
		},
	},
}

export const lightThemeOptions: ThemeOptions = {
	palette: {
		mode: 'light',
		primary: {
			main: brandColors.primaryAction, // #621984
			contrastText: brandColors.primaryContrast, // #FFFDFD
			dark: '#44115C',
			light: brandColors.primaryLight, // #81479C
		},
		secondary: {
			main: brandColors.hexGrid, // #878DDD
			contrastText: brandColors.primaryContrast, // #FFFDFD
			dark: '#5F64B5',
			light: '#A3A7E5',
		},
		text: {
			primary: brandColors.primary, // #3A2E2F
			secondary: '#938586',
			disabled: '#C8C2C3',
		},
		error: {
			main: '#792121',
			dark: '#541717',
			light: '#934D4D',
		},
		warning: {
			main: '#A65900',
			contrastText: brandColors.primaryContrast, // #FFFDFD
			dark: '#743E00',
			light: '#B77A33',
		},
		info: {
			main: brandColors.hexGrid, // #878DDD
			dark: '#5F64B5',
			light: '#A3A7E5',
		},
		success: {
			main: '#2E623F',
			contrastText: brandColors.primary, // #3A2E2F
			light: '#578165',
			dark: '#20442C',
		},
		divider: 'rgba(58, 46, 47, 0.42)',
		action: {
			hover: 'rgba(98, 25, 132, 0.08)',
			hoverOpacity: 0.08,
			focus: brandColors.primaryLight, // #81479C
			focusOpacity: 0.12,
			selected: brandColors.primary, // #3A2E2F
			selectedOpacity: 0.08,
		},
		background: {
			default: brandColors.primaryContrast, // #FFFDFD
			paper: '#FBF8F8', // Slightly warmer off-white for elevation
		},
		...referenceFieldColors,
	},
	typography: {
		fontFamily: 'Telegraf',
		subtitle1: {
			fontWeight: 600,
		},
	},
	components: componentOverrides,
}

export const darkThemeOptions: ThemeOptions = {
	palette: {
		mode: 'dark',
		primary: {
			main: brandColors.primaryLight, // #81479C in dark mode
			contrastText: '#FFFFFF',
			light: '#A16AB4',
			dark: brandColors.primaryAction, // #621984
		},
		secondary: {
			main: '#B8BEFE',
			contrastText: brandColors.primary, // #3A2E2F
			dark: '#8085B1',
			light: '#C6CBFE',
		},
		text: {
			primary: '#FFFFFF',
			secondary: 'rgba(255, 255, 255, 0.7)',
			disabled: 'rgba(255, 255, 255, 0.5)',
		},
		error: {
			main: '#E32458',
			dark: '#9E193D',
			light: '#E84F79',
		},
		warning: {
			main: '#F78D1E',
			contrastText: brandColors.primary, // #3A2E2F
			dark: '#F57C00',
			light: '#F8A34B',
		},
		info: {
			main: brandColors.hexGrid, // #878DDD
			dark: '#5F64B5',
			light: '#A3A7E5',
		},
		success: {
			main: '#67C599',
			contrastText: brandColors.primary, // #3A2E2F
			light: '#85D0AD',
			dark: '#48896B',
		},
		divider: 'rgba(255, 255, 255, 0.12)',
		action: {
			active: 'rgba(129, 71, 156, 0.30)', // Using primaryLight
			activatedOpacity: 0.3,
			hover: 'rgba(129, 71, 156, 0.16)', // Using primaryLight
			hoverOpacity: 0.16,
			focus: brandColors.primaryAction, // #621984
			focusOpacity: 0.12,
			selected: '#FFFFFF',
			selectedOpacity: 0.16,
			disabled: brandColors.primaryLight, // #81479C
			disabledOpacity: 0.3,
			disabledBackground: '#FFFFFF',
		},
		background: {
			default: brandColors.primary, // #3A2E2F in dark mode
			paper: brandColors.primary, // #3A2E2F
		},
		...referenceFieldColors,
	},
	typography: {
		fontFamily: 'Telegraf',
	},
	components: componentOverrides,
}

export const themeOptionsMap = {
	[Theme.Light]: lightThemeOptions,
	[Theme.Dark]: darkThemeOptions,
}
